import {
  BascAlert,
  BascBox,
  BascButton,
  BascCard,
  BascCenter,
  BascGroup,
  BascLanguageSelector,
  BascLoader,
  BascStack,
  BascText,
  BascTextInput,
  useTranslation,
} from '@bco/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { loadClientAssets } from '../../federationInit';
import { buildLoginBackgroundStyle } from '../../utils/loginBackgroundStyle';
import './verify-email.css';

interface ClientAssets {
  appLogo?: string;
  appLogoDark?: string;
  loginBackground?: string;
}

type VerifyState = 'loading' | 'success' | 'error' | 'resend';

export function VerifyEmailPage() {
  const { clientName = '' } = useParams<{ clientName: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = searchParams.get('token');

  const [assets, setAssets] = useState<ClientAssets | null>(null);
  const [state, setState] = useState<VerifyState>('loading');
  const [error, setError] = useState<string>('');
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    async function loadAssets() {
      const clientAssets = await loadClientAssets(clientName);
      setAssets(clientAssets);
    }
    if (clientName) {
      loadAssets();
    }
  }, [clientName]);

  useEffect(() => {
    const controller = new AbortController();
    let redirectTimer: ReturnType<typeof setTimeout> | null = null;

    async function verifyEmail() {
      if (!token) {
        setState('resend');
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_GATEWAY_URL || ''}/api/auth/verify-email?token=${token}`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || t('auth.verificationFailed'));
        }

        setState('success');

        redirectTimer = setTimeout(() => {
          navigate(`/${clientName}/login`);
        }, 3000);
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        setState('error');
        setError(
          err instanceof Error ? err.message : t('auth.verificationFailed')
        );
      }
    }

    verifyEmail();

    return () => {
      controller.abort();
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [token, clientName, navigate]);

  const handleResend = async () => {
    if (!resendEmail) {
      return;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_GATEWAY_URL || ''}/api/auth/resend-verification`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resendEmail, clientName }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || t('errors.resendFailed'));
      }

      setResendSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.resendFailed'));
    } finally {
      setIsResending(false);
    }
  };

  const backgroundStyle = buildLoginBackgroundStyle(assets);

  return (
    <BascBox className="verify-email-page" style={backgroundStyle}>
      <BascBox className="verify-email-overlay" />
      <BascCenter className="verify-email-container">
        <BascCard
          className="verify-email-card"
          shadow="xl"
          padding="xl"
          radius="md"
        >
          <BascStack gap="lg" align="center">
            {assets?.appLogo && (
              <BrandLogo
                src={assets.appLogo}
                srcDark={assets.appLogoDark}
                alt="BCO PORTAL"
                className="verify-email-logo"
              />
            )}

            {state === 'loading' && (
              <>
                <BascLoader size="lg" />
                <BascText textRole="heading2" fw={500}>
                  {t('auth.verifyingEmail')}
                </BascText>
              </>
            )}

            {state === 'success' && (
              <>
                <BascText textRole="heading1" fw={600} c="green" ta="center">
                  {t('auth.emailVerified')}
                </BascText>
                <BascText ta="center" c="dimmed">
                  {t('auth.emailVerifiedDesc')}
                </BascText>
                <BascButton
                  emphasis="primary"
                  onClick={() => navigate(`/${clientName}/login`)}
                >
                  {t('goToLogin')}
                </BascButton>
              </>
            )}

            {state === 'error' && (
              <>
                <BascText textRole="heading1" fw={600} c="red" ta="center">
                  {t('auth.verificationFailed')}
                </BascText>
                <BascAlert color="red" variant="light">
                  {error}
                </BascAlert>
                <BascText ta="center" c="dimmed">
                  {t('auth.verificationFailedDesc')}
                </BascText>
                <BascButton variant="subtle" onClick={() => setState('resend')}>
                  {t('auth.resendVerification')}
                </BascButton>
                <BascButton
                  variant="subtle"
                  onClick={() => navigate(`/${clientName}/login`)}
                >
                  {t('backToLogin')}
                </BascButton>
              </>
            )}

            {state === 'resend' && (
              <>
                <BascText textRole="heading1" fw={600} ta="center">
                  {t('auth.resendVerification')}
                </BascText>

                {resendSuccess ? (
                  <>
                    <BascAlert color="green" variant="light">
                      {t('auth.verificationSent')}
                    </BascAlert>
                    <BascButton
                      emphasis="primary"
                      onClick={() => navigate(`/${clientName}/login`)}
                    >
                      {t('backToLogin')}
                    </BascButton>
                  </>
                ) : (
                  <>
                    <BascText ta="center" c="dimmed">
                      {t('auth.resendVerificationDesc')}
                    </BascText>

                    {error && (
                      <BascAlert
                        color="red"
                        variant="light"
                        onClose={() => setError('')}
                        withCloseButton
                      >
                        {error}
                      </BascAlert>
                    )}

                    <BascTextInput
                      label={t('auth.email')}
                      placeholder="your@email.com"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.currentTarget.value)}
                      type="email"
                      required
                      style={{ width: '100%' }}
                    />

                    <BascGroup>
                      <BascButton
                        variant="subtle"
                        onClick={() => navigate(`/${clientName}/login`)}
                        disabled={isResending}
                      >
                        {t('backToLogin')}
                      </BascButton>
                      <BascButton
                        emphasis="primary"
                        onClick={handleResend}
                        loading={isResending}
                        disabled={!resendEmail}
                      >
                        {t('auth.sendVerificationEmail')}
                      </BascButton>
                    </BascGroup>
                  </>
                )}
              </>
            )}

            {/* Language Selector */}
            <BascGroup justify="center">
              <BascLanguageSelector compact label={t('language.change')} />
            </BascGroup>
          </BascStack>
        </BascCard>
      </BascCenter>
    </BascBox>
  );
}

export default VerifyEmailPage;
